import { useCallback, useEffect, useMemo, useState } from "react";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import { createAttendance, getAttendanceList, getStaffDailyAttendanceList, updateAttendance } from "../../../../services/workspace/attendance.service";
import BottomDrawer from "../../common/drawer/bottomDrawer";
import CommonForm from "../../../components/form/form";
import PaginationComponent from "../../../components/table/tablePagination";
import {
  _mark_for,
  convertToLabelValue,
  status_type,
} from "../../../../utils/common";
import { getLeaveTypeList } from "../../../../services/workspace/leaveType.service";
import toast from "react-hot-toast";
import DateFilter from "../../../components/filter/dateFilter";
import {
  HiClock,
  HiCheckCircle,
  HiXCircle,
  HiUserCircle,
  HiPencil,
  HiViewGrid,
  HiViewList
} from "react-icons/hi";
import { NoDataFound } from "../../../components/table/tableComponent";
import AttendanceTable from "../../../components/table/attendanceTable";
import moment from "moment";
import { createStaffAttendance, updateStaffAttendance } from "../../../../services/workspace/staff.service";

const items = [{ label: "Staff Daily Attendance", path: "" }];

// Helper function to get initials from name
const getInitials = (name) => {
  if (!name || name === "-") return "?";
  const words = name.trim().split(" ");
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

// Helper function to get color based on name (for consistent avatar colors)
const getAvatarColor = (name) => {
  if (!name || name === "-") return "bg-gray-500";
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-yellow-500",
    "bg-red-500",
    "bg-teal-500",
  ];
  const index = name.charCodeAt(0) % colors.length;
  return colors[index];
};

const StaffDailyAttendance = () => {
  const [isOpenSheet, setIsOpenSheet] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [leaveTypeList, setLeaveTypeList] = useState([]);
  const [date, setDate] = useState(new Date());
  const [viewType, setViewType] = useState("table"); // "table" or "card"

  const handleClose = () => {
    setIsOpenSheet(false);
    setCurrentRecord(null);
  };
  const dataTransformer = useCallback((data) => {
    if (!Array.isArray(data)) return { data: [], action: {} };

    const transformedData = data.map((item) => {
      const attendance = item.attendance;

      return {
        staffName: {
          name: item.name,
          staffId: item.code || "-",
          photo: item.image || "/default-profile.png",
        },
        department: item?.department?.name || "-",
        designation: item?.designation?.name || "-",
        date: item?.attendance?.date || "-",

        start_time: item?.attendance?.start_time || "-",
        end_time: item?.attendance?.end_time || "-",

        check: item?.attendance
          ? `${item?.attendance?.start_time || "-"} - ${item?.attendance?.end_time || "-"
          }`
          : "-", 
        totalHours: item?.attendance?.total_hours || "-",
        remarks: item?.attendance?.remarks || "-",
        // Preserve the full attendance object for form initialization
        attendance: attendance || null,
        // Also preserve the original item data for reference
        originalItem: item,
      };
    });
    const actionButton = {
      edit: (item) => {
        setIsOpenSheet(true);
        setCurrentRecord(item);
      },
    };
    return {
      data: transformedData,
      action: actionButton,
    };
  }, []);

  // ✅ Memoize the config object to prevent unnecessary refetch
  const fetchConfig = useMemo(
    () => ({
      dataPath: "user.data",
      totalPath: "user.meta.total",
      errorMessage: "Failed to fetch user data!",
      debounceDelay: 500,

      customParams: { with: "attendance,department,designation", date },
      sortingOptions: [{ label: "Name", field: "name" }],
      sortingModule: "attendance",
      orderParamNames: {
        orderByKey: "order_by",
        orderTypeKey: "order_type",
        includeDisabledKey: "includeDisabled",
      },
      sortingDisableText: "Disabled",
      sortingShowDisableToggle: false,
      dataTransformer,
    }),
    [dataTransformer, date]
  );

  const {
    loading,
    data: tableData,
    total: totalItems,
    sortingUIProps,
    fetchData,
    refreshData,
    limit,
    currentPage,
    handlePageChange,
  } = useCommonFetch(getStaffDailyAttendanceList, fetchConfig);

  // Helper function to convert time from HH:mm to H:i:s format (for API)
  const convertTimeToApiFormat = (time) => {
    if (!time || time === "" || time === "-") return "";

    // If already in H:i:s format (has 3 parts), return as is
    const parts = time.split(":");
    if (parts.length === 3) {
      return time;
    }

    // If in HH:mm format (has 2 parts), add :00 for seconds
    if (parts.length === 2) {
      return `${time}:00`;
    }

    // If invalid format, return empty string
    return "";
  };

  // Helper function to convert time from H:i:s to HH:mm format (for form input)
  const convertTimeFromApiFormat = (time) => {
    if (!time || time === "" || time === "-") return "";

    // If in H:i:s format (has 3 parts), return only HH:mm
    const parts = time.split(":");
    if (parts.length === 3) {
      return `${parts[0]}:${parts[1]}`;
    }

    // If already in HH:mm format (has 2 parts), return as is
    if (parts.length === 2) {
      return time;
    }

    // If invalid format, return empty string
    return "";
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const attendance = currentRecord?.attendance || currentRecord?.originalItem?.attendance;
      const attendanceId = attendance?.uuid;

      // Get the correct date - prefer attendance date, fallback to selected date
      let attendanceDate = attendance?.date || currentRecord?.date;
      if (!attendanceDate || attendanceDate === "-" || attendanceDate === null || attendanceDate === undefined) {
        attendanceDate = moment(date).format("YYYY-MM-DD");
      }

      // Convert time values to H:i:s format
      const startTime = convertTimeToApiFormat(values["check-in-time"]);
      const endTime = convertTimeToApiFormat(values["check-out-time"]);

      const payload = {
        user_id: currentRecord?.originalItem?.id || currentRecord?.staffName?.staffId,
        date: attendanceDate,
        start_time: startTime,
        end_time: endTime,
        status: values["status"],
        marked_for: values["deduction-type"],
        leave_type_id: values["leave-type"],
        remarks: values["remarks"]
      };

      // Call API based on whether updating or creating
      if (attendanceId) {
        await updateAttendance(attendanceId, payload);
        toast.success("Attendance updated successfully!");
      } else {
        await createAttendance(payload);
        toast.success("Attendance marked successfully!");
      }

      // Refresh the data
      refreshData({ date }, true);
      resetForm();
      setIsOpenSheet(false);
      setCurrentRecord(null);
    } catch (error) {
      console.error("Failed to update attendance:", error);
      toast.error(error?.response?.data?.message || "Failed to update attendance. Please try again.");
      // toast.error("Failed to update attendance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchLeaveType = () => {
    getLeaveTypeList()
      .then((response) => {
        setLeaveTypeList(
          convertToLabelValue(response.leaveType.data || []) || []
        );
      })
      .catch((error) => {
        toast.error("Failed to fetch leave type data!");
      });
  };

  const handlePrev = (date) => {
    const prevDate = new Date(date);
    prevDate.setDate(prevDate.getDate() - 1);
    setDate(prevDate);
  };

  const handleNext = (date) => {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + 1);
    setDate(nextDate);
  };

  useEffect(() => {
    fetchLeaveType();
  }, []);

  useEffect(() => {

    // customParams: { with: "attendance,department,designation", date },
    const customParams = { with: "attendance,department,designation", date }

    refreshData({ date }, true);
  }, [date, refreshData, fetchConfig]);

  const comprehensiveFields = [
    {
      name: "check-in-time",
      label: "Check-in Time",
      type: "time",
      required: false, // Will be conditionally required based on status
      colSpan: 6,
      showIf: (values) => values["status"] === "present",
      validation: (rule) => {
        return rule.when("status", {
          is: (status) => status === "present",
          then: (schema) => schema.required("Check-in Time is required when status is Present"),
          otherwise: (schema) => schema.notRequired(),
        });
      },
    },
    {
      name: "check-out-time",
      label: "Check-out Time",
      type: "time",
      required: false, // Will be conditionally required based on status
      colSpan: 6,
      showIf: (values) => values["status"] === "present",
      validation: (rule) => {
        return rule.when("status", {
          is: (status) => status === "present",
          then: (schema) => schema.required("Check-out Time is required when status is Present"),
          otherwise: (schema) => schema.notRequired(),
        });
      },
    },
    {
      name: "status",
      label: "Update Status",
      type: "radio",
      required: true,
      colSpan: 12,
      options: status_type,
    },
    {
      name: "deduction-type",
      label: "Marked For",
      type: "select",
      required: true,
      colSpan: 12,
      options: _mark_for,
    },
    {
      name: "leave-type",
      label: "Marked Rest Day",
      type: "select",
      required: false, // Will be conditionally required based on validation
      colSpan: 12,
      options: leaveTypeList,
      showIf: (values) =>
        values["status"] === "leave" ||
        (values["status"] === "present" && values["deduction-type"] !== 1),
      validation: (rule) => {
        return rule.when(["status", "deduction-type"], {
          is: (status, deductionType) => {
            // Required when: status is "leave" OR (status is "present" AND deduction-type is NOT 1 (Full Day))
            return status === "leave" || (status === "present" && deductionType !== 1 && deductionType !== "1");
          },
          then: (schema) => schema.required("Marked Rest Day is required"),
          otherwise: (schema) => schema.notRequired(),
        });
      },
    },
    {
      name: "remarks",
      label: "Remarks",
      type: "textarea",
      required: true,
      colSpan: 12,
    },
  ];

  const headers = [
    { name: "Staff Name", key: "staffName" },
    {
      connecter: [{ name: "In Time" }, { name: "Out Time" }],
    },
    { name: "Total Hours", key: "totalHours", last: true },
    { name: "Remarks", key: "remarks", last: true },
  ];

  // Get the attendance date - prefer attendance date, fallback to selected date
  const getAttendanceDate = () => {
    if (!currentRecord) return moment(date).format("YYYY-MM-DD");

    const attendance = currentRecord?.attendance || currentRecord?.originalItem?.attendance;
    const attendanceDate = attendance?.date || currentRecord?.date;

    // If date is "-" or invalid, use the selected date from date filter
    if (attendanceDate && attendanceDate !== "-" && attendanceDate !== null && attendanceDate !== undefined) {
      return attendanceDate;
    }

    return moment(date).format("YYYY-MM-DD");
  };

  const data = [
    { label: "Department", value: currentRecord?.department || "-" },
    { label: "Designation", value: currentRecord?.designation || "-" },
    { label: "Date", value: getAttendanceDate() },
  ];

  // Prepare initial data for the form based on currentRecord
  const formInitialData = useMemo(() => {
    if (!currentRecord) return {};

    // Try multiple sources for attendance data
    const attendance = currentRecord?.attendance || currentRecord?.originalItem?.attendance;
    const originalItem = currentRecord?.originalItem;

    // Get start_time and end_time, handling "-" values and null
    let startTime = attendance?.start_time || currentRecord?.start_time || "";
    let endTime = attendance?.end_time || currentRecord?.end_time || "";

    // Clean up time values
    if (startTime === "-" || startTime === null || startTime === undefined) startTime = "";
    if (endTime === "-" || endTime === null || endTime === undefined) endTime = "";

    // Convert from API format (H:i:s) to form input format (HH:mm)
    startTime = convertTimeFromApiFormat(startTime);
    endTime = convertTimeFromApiFormat(endTime);

    // Get status - handle both string and number formats, normalize to match status_type values
    let status = attendance?.status || "";
    if (status === 1 || status === "1" || status === "Present" || status === "present") {
      status = "present";
    } else if (status === 0 || status === "0" || status === "Absent" || status === "absent") {
      status = "absent";
    } else if (status === "Leave" || status === "leave") {
      status = "leave";
    } else {
      status = status || "";
    }

    // Get marked_for (deduction-type) - should match _mark_for value (1, 2, or 3)
    let markedFor = attendance?.marked_for;
    if (markedFor === null || markedFor === undefined || markedFor === "-") {
      markedFor = "";
    } else {
      // Ensure it's a number to match the value in _mark_for options
      markedFor = Number(markedFor);
    }

    // Get leave_type_id - should be string/number to match leaveTypeList options
    let leaveTypeId = attendance?.leave_type_id;
    if (leaveTypeId === null || leaveTypeId === undefined || leaveTypeId === "-") {
      leaveTypeId = "";
    } else {
      leaveTypeId = String(leaveTypeId);
    }

    // Get remarks
    let remarks = attendance?.remarks || "";
    if (remarks === "-" || remarks === null || remarks === undefined) {
      remarks = "";
    }

    const initialData = {
      "check-in-time": startTime,
      "check-out-time": endTime,
      "status": status,
      "deduction-type": markedFor !== "" ? markedFor : "",
      "leave-type": leaveTypeId,
      "remarks": remarks,
    };

    return initialData;
  }, [currentRecord]);

  return (
    <div className="flex flex-col gap-4 min-h-screen">
      {/* Header Section with Enhanced Design */}
      <div className="bg-white ">
        <PageTableHeader
          isSearch
          filter={false}
          shorting
          breadcrumbs={items}
          sortingModule="attendance"
          onSortingChange={sortingUIProps?.onSortingChange}
          sortingProps={sortingUIProps}
          buttons={
            <div className="flex items-center gap-3">
              <DateFilter
                currentDate={date}
                onPrevPress={() => {
                  handlePrev(date);
                }}
                onNextPress={() => {
                  handleNext(date);
                }}
              />
              {/* View Toggle Button */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewType("table")}
                  className={`p-2 rounded-md transition-all duration-200 ${viewType === "table"
                      ? "bg-white text-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                    }`}
                  title="Table View"
                >
                  <HiViewList className="text-xl" />
                </button>
                <button
                  onClick={() => setViewType("card")}
                  className={`p-2 rounded-md transition-all duration-200 ${viewType === "card"
                      ? "bg-white text-blue-600 "
                      : "text-gray-600 hover:text-gray-900"
                    }`}
                  title="Card View"
                >
                  <HiViewGrid className="text-xl" />
                </button>
              </div>
            </div>
          }
        />
      </div>

      {/* Summary Statistics - Only show in card view */}
      {!loading && tableData?.data && tableData.data.length > 0 && viewType === "card" && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {(() => {
            const stats = {
              total: tableData.data.length,
              present: tableData.data.filter(
                (item) =>
                  item.start_time &&
                  item.start_time !== "-" &&
                  item.end_time &&
                  item.end_time !== "-"
              ).length,
              absent: tableData.data.filter(
                (item) => !item.start_time || item.start_time === "-"
              ).length,
              partial: tableData.data.filter(
                (item) =>
                  (item.start_time && item.start_time !== "-" && (!item.end_time || item.end_time === "-")) ||
                  (!item.start_time || item.start_time === "-")
              ).length - tableData.data.filter(
                (item) => !item.start_time || item.start_time === "-"
              ).length,
            };

            return (
              <>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-blue-600 font-medium mb-1">
                        Total Staff
                      </p>
                      <p className="text-2xl font-bold text-blue-900">
                        {stats.total}
                      </p>
                    </div>
                    <HiUserCircle className="text-3xl text-blue-400 opacity-50" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-green-600 font-medium mb-1">
                        Present
                      </p>
                      <p className="text-2xl font-bold text-green-900">
                        {stats.present}
                      </p>
                    </div>
                    <HiCheckCircle className="text-3xl text-green-400 opacity-50" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-red-600 font-medium mb-1">
                        Absent
                      </p>
                      <p className="text-2xl font-bold text-red-900">
                        {stats.absent}
                      </p>
                    </div>
                    <HiXCircle className="text-3xl text-red-400 opacity-50" />
                  </div>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 border border-yellow-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-yellow-600 font-medium mb-1">
                        Partial
                      </p>
                      <p className="text-2xl font-bold text-yellow-900">
                        {stats.partial}
                      </p>
                    </div>
                    <HiClock className="text-3xl text-yellow-400 opacity-50" />
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Attendance Display - Table or Card View */}
      {viewType === "table" ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <AttendanceTable
            attendanceData={tableData?.data || []}
            getStatusStyles={getStatusStyles}
            loading={loading}
            headers={headers}
            action={tableData?.action}
          />
          <PaginationComponent
            itemPerPage={limit}
            totalItems={totalItems}
            paginate={handlePageChange}
            currentPage={currentPage}
            loading={loading}
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, idx) => (
                <div
                  key={idx}
                  className="animate-pulse bg-gray-100 rounded-xl h-48"
                />
              ))}
            </div>
          ) : !tableData?.data || tableData.data.length === 0 ? (
            <NoDataFound asTableCell={false} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {tableData.data.map((item, index) => {
                const isPresent = item.start_time && item.start_time !== "-" && item.end_time && item.end_time !== "-";
                const isAbsent = !item.start_time || item.start_time === "-";
                const status = isPresent ? "Present" : isAbsent ? "Absent" : "Partial";

                // Calculate hours worked
                let hoursWorked = "-";
                if (isPresent) {
                  try {
                    const start = moment(item.start_time, "HH:mm:ss");
                    const end = moment(item.end_time, "HH:mm:ss");
                    const diff = end.diff(start, 'hours', true);
                    hoursWorked = diff.toFixed(1) + "h";
                  } catch (e) {
                    hoursWorked = item.totalHours || "-";
                  }
                }

                const statusConfig = getStatusStyles(status);

                return (
                  <div
                    key={index}
                    onClick={() => {
                      setIsOpenSheet(true);
                      setCurrentRecord(item);
                    }}
                    className="group relative bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-blue-400 hover:shadow-lg transition-all duration-300 cursor-pointer"
                  >
                    {/* Status Badge */}
                    <div className="absolute top-4 right-4">
                      <div
                        className={`w-3 h-3 rounded-full ${statusConfig.bgColor} shadow-sm`}
                      />
                    </div>

                    {/* Profile Section */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="relative">
                        <div className="w-14 h-14 rounded-full overflow-hidden ring-2 ring-gray-100 flex items-center justify-center relative">
                          {item.staffName?.photo && item.staffName.photo !== "/default-profile.png" && item.staffName.photo !== "" ? (
                            <>
                              <img
                                src={item.staffName.photo}
                                alt={item.staffName?.name}
                                className="w-full h-full object-cover absolute inset-0"
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  const avatarDiv = e.target.nextSibling;
                                  if (avatarDiv) {
                                    avatarDiv.classList.remove("hidden");
                                  }
                                }}
                              />
                              <div
                                className={`w-full h-full ${getAvatarColor(item.staffName?.name)} flex items-center justify-center text-white font-semibold text-lg hidden absolute inset-0`}
                              >
                                {getInitials(item.staffName?.name)}
                              </div>
                            </>
                          ) : (
                            <div
                              className={`w-full h-full ${getAvatarColor(item.staffName?.name)} flex items-center justify-center text-white font-semibold text-lg`}
                            >
                              {getInitials(item.staffName?.name)}
                            </div>
                          )}
                        </div>
                        {isPresent && (
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm truncate">
                          {item.staffName?.name || "-"}
                        </h3>
                        <p className="text-xs text-gray-500 truncate">
                          {item.staffName?.staffId || "-"}
                        </p>
                      </div>
                    </div>

                    {/* Attendance Details */}
                    <div className="space-y-3">
                      {/* Time Section */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <HiClock className="text-gray-400 text-sm" />
                            <span className="text-xs text-gray-600 font-medium">
                              Check In/Out
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-1">In Time</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {item.start_time !== "-" ? item.start_time : "—"}
                            </p>
                          </div>
                          <div className="w-px h-8 bg-gray-300 mx-2" />
                          <div className="flex-1 text-right">
                            <p className="text-xs text-gray-500 mb-1">Out Time</p>
                            <p className="text-sm font-semibold text-gray-900">
                              {item.end_time !== "-" ? item.end_time : "—"}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Status & Hours */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isPresent ? (
                            <HiCheckCircle className="text-green-500 text-lg" />
                          ) : (
                            <HiXCircle className="text-red-500 text-lg" />
                          )}
                          <span
                            className={`text-sm font-semibold ${statusConfig.textColor}`}
                          >
                            {status}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Total Hours</p>
                          <p className="text-sm font-bold text-gray-900">
                            {hoursWorked}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Edit Button on Hover */}
                    <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-blue-500 text-white rounded-full p-2 shadow-md">
                        <HiPencil className="text-sm" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Enhanced Drawer for Attendance Update */}
      <BottomDrawer
        isOpen={isOpenSheet}
        setIsOpen={handleClose}
        title="Update Attendance"
        size={"600px"}
        position="right"
      >
        <div className="flex flex-col gap-6 p-2">
          {/* Staff Profile Header Card */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="rounded-full overflow-hidden w-20 h-20 ring-4 ring-white shadow-md flex items-center justify-center relative">
                  {(currentRecord?.photo || currentRecord?.staffName?.photo) &&
                    currentRecord?.staffName?.photo !== "/default-profile.png" &&
                    currentRecord?.staffName?.photo !== "" ? (
                    <>
                      <img
                        src={currentRecord?.photo || currentRecord?.staffName?.photo}
                        alt="Staff Profile"
                        className="w-full h-full object-cover absolute inset-0"
                        onError={(e) => {
                          e.target.style.display = "none";
                          const avatarDiv = e.target.nextSibling;
                          if (avatarDiv) {
                            avatarDiv.classList.remove("hidden");
                          }
                        }}
                      />
                      <div
                        className={`w-full h-full ${getAvatarColor(currentRecord?.staffName?.name)} flex items-center justify-center text-white font-bold text-2xl hidden absolute inset-0`}
                      >
                        {getInitials(currentRecord?.staffName?.name)}
                      </div>
                    </>
                  ) : (
                    <div
                      className={`w-full h-full ${getAvatarColor(currentRecord?.staffName?.name)} flex items-center justify-center text-white font-bold text-2xl`}
                    >
                      {getInitials(currentRecord?.staffName?.name)}
                    </div>
                  )}
                </div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <h3 className="text-xl font-bold text-gray-900">
                  {currentRecord?.staffName?.name || "-"}
                </h3>
                <p className="text-sm text-gray-600 font-medium">
                  {currentRecord?.staffName?.staffId || "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Information Cards Grid */}
          <div className="grid grid-cols-3 gap-3">
            {data
              .filter((key) => key.label !== "days" && key.label !== "Staff ID")
              .map((data, index) => (
                <div
                  key={index}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:bg-gray-100 transition-colors duration-200"
                >
                  <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
                    {data?.label}
                  </p>
                  <p className="text-base font-semibold text-gray-900">
                    {data?.value}
                  </p>
                </div>
              ))}
          </div>

          {/* Form Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <h4 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wide">
              Attendance Details
            </h4>
            <CommonForm
              fields={comprehensiveFields}
              initialData={formInitialData}
              key={isOpenSheet && currentRecord ?
                (currentRecord?.attendance?.id ||
                  currentRecord?.originalItem?.attendance?.id ||
                  currentRecord?.originalItem?.id ||
                  currentRecord?.staffName?.staffId ||
                  "new") : "closed"}
              enableReinitialize={true}
              onSubmit={handleSubmit}
              showSubmitButton={true}
              className="bg-transparent"
            />
          </div>
        </div>
      </BottomDrawer>
    </div>
  );
};

export const getStatusStyles = (status) => {
  switch (status) {
    case 1:
    case "Present":
      return { bgColor: "bg-green-500", textColor: "text-green-500" };
    case 0:
    case "Absent":
      return { bgColor: "bg-red-500", textColor: "text-red-500" };
    case "Partial":
      return { bgColor: "bg-yellow-500", textColor: "text-yellow-600" };
    case "Half Day":
      return { bgColor: "bg-yellow-400", textColor: "text-yellow-400" };
    default:
      return { bgColor: "bg-gray-400", textColor: "text-gray-400" };
  }
};

export default StaffDailyAttendance;
